import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import {
  formatDateTime,
  formatDuracaoSegundos,
  formatHoraMinuto,
  SituacaoTranscricaoType,
} from '../../../../../../_metronic/helpers'
import { IconPaths } from '../../../../../../_metronic/helpers/enumsUI'
import { Button } from '../../../../../components/Button/Button'
import { InfoAlert } from '../../../../../components/Info/InfoAlert'
import { type TranscricaoAtendimentoView } from '../../../atendimento/core/_models'

type Props = {
  isLoading: boolean
  sessoes: TranscricaoAtendimentoView[]
  erro?: boolean
}

type FalaTranscricao = {
  id: number
  timestamp?: string
  speaker: string
  texto: string
}

const LINE_REGEX = /^\[(\d{1,2}:\d{2}(?::\d{2})?)\]\s*([^:]+):\s*(.*)$/

function parseFalas(textoBruto: string): FalaTranscricao[] {
  const linhas = textoBruto.split(/\r?\n/)
  const falas: FalaTranscricao[] = []
  let idCounter = 1

  for (const linha of linhas) {
    const trimmed = linha.trim()
    if (!trimmed) continue

    const match = trimmed.match(LINE_REGEX)
    if (match) {
      const [, timestamp, speaker, texto] = match

      falas.push({
        id: idCounter++,
        timestamp,
        speaker: speaker.trim(),
        texto: texto.trim(),
      })
    } else {
      if (falas.length > 0) {
        falas[falas.length - 1].texto += `\n${trimmed}`
      } else {
        falas.push({
          id: idCounter++,
          speaker: 'Transcrição',
          texto: trimmed,
        })
      }
    }
  }

  return falas
}

type Falante = 'medico' | 'paciente' | 'participante'

// O rótulo vem do backend: "Médico", "Médico (Nome)", "Paciente", "Paciente (Nome)" ou
// "Participante". Por isso o prefixo manda: um paciente chamado "Ana Medico" não vira médico.
const classificaFalante = (speaker: string): Falante => {
  const normalizado = speaker.trim().toLowerCase()
  if (normalizado.startsWith('médico') || normalizado.startsWith('medico')) return 'medico'
  if (normalizado.startsWith('paciente')) return 'paciente'
  return 'participante'
}

const BolhaFala: FC<{ fala: FalaTranscricao }> = ({ fala }) => {
  const falante = classificaFalante(fala.speaker)
  const medico = falante === 'medico'
  const classeSelo = falante === 'paciente' ? 'atd-badge-info' : 'atd-badge-secondary'
  const classeBorda = medico ? 'border-primary border-opacity-25' : 'border-gray-300'
  const horario = fala.timestamp ? (
    <span className='fs-8 text-gray-700'>[{fala.timestamp}]</span>
  ) : null

  return (
    <div
      className={`d-flex flex-column ${medico ? 'align-items-end' : 'align-items-start'}`}
      data-falante={falante}
    >
      <div className={`d-flex align-items-center gap-2 mb-1 ${medico ? 'me-1' : 'ms-1'}`}>
        {medico && horario}
        <span className={`badge fw-semibold fs-8 ${classeSelo}`}>{fala.speaker}</span>
        {!medico && horario}
      </div>
      <div
        className={`p-3 rounded atd-transcricao-balao text-gray-800 border shadow-xs mw-75 text-break pre-line ${classeBorda}`}
      >
        <span className='fs-7'>{fala.texto}</span>
      </div>
    </div>
  )
}

const MENSAGEM_COPIA_FALHOU = 'Não foi possível copiar. Selecione o texto e copie manualmente.'

const ConversaTranscricao: FC<{ texto: string }> = ({ texto }) => {
  const [copiado, setCopiado] = useState(false)
  const [falhaCopia, setFalhaCopia] = useState(false)
  const [visualizacaoConversa, setVisualizacaoConversa] = useState(true)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const falas = useMemo(() => parseFalas(texto), [texto])

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    },
    []
  )

  const handleCopiar = async () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    try {
      await navigator.clipboard.writeText(texto)
      setFalhaCopia(false)
      setCopiado(true)
      timeoutRef.current = setTimeout(() => setCopiado(false), 2000)
    } catch {
      setCopiado(false)
      setFalhaCopia(true)
    }
  }

  return (
    <div className='row'>
      <div className='col-12 d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2'>
        <div className='d-flex align-items-center gap-2'>
          <span className='badge atd-badge-secondary fs-8'>
            {visualizacaoConversa ? 'Formato Conversa' : 'Texto Corrido'}
          </span>
        </div>
        <div className='d-flex align-items-center gap-2'>
          <Button
            size='btn-sm'
            variant='secondary-outline'
            icon={visualizacaoConversa ? IconPaths.Notas : IconPaths.Sms}
            onClick={() => setVisualizacaoConversa(!visualizacaoConversa)}
            title={visualizacaoConversa ? 'Alternar para texto corrido' : 'Alternar para conversa'}
          >
            {visualizacaoConversa ? 'Texto Corrido' : 'Conversa'}
          </Button>
          <Button
            size='btn-sm'
            variant='secondary-outline'
            icon={copiado ? IconPaths.Check : IconPaths.Copy}
            onClick={handleCopiar}
            title='Copiar Transcrição'
          >
            Copiar Transcrição
          </Button>
        </div>
        <div role='status' className='w-100 fs-8 text-gray-700 text-end'>
          {copiado ? 'Copiado!' : falhaCopia ? MENSAGEM_COPIA_FALHOU : ''}
        </div>
      </div>

      <div className='col-12'>
        {visualizacaoConversa && falas.length > 0 ? (
          <div
            className='p-4 bg-light rounded border border-gray-300 mh-400px scroll-y d-flex flex-column gap-3'
            data-testid='transcricao-conversa'
          >
            {falas.map((fala) => (
              <BolhaFala key={fala.id} fala={fala} />
            ))}
          </div>
        ) : (
          <div className='p-4 bg-light rounded border border-gray-300 text-gray-800 mh-300px scroll-y fs-7 lh-base pre-line text-break'>
            {texto}
          </div>
        )}
      </div>
    </div>
  )
}

const SITUACAO_SEM_TEXTO: Partial<Record<SituacaoTranscricaoType, string>> = {
  [SituacaoTranscricaoType.Transcrevendo]: 'Em andamento',
  [SituacaoTranscricaoType.Processando]: 'Em processamento',
  [SituacaoTranscricaoType.Erro]: 'Indisponível',
}

type SessaoComTexto = TranscricaoAtendimentoView & { textoConsolidado: string }

const temTexto = (sessao: TranscricaoAtendimentoView): sessao is SessaoComTexto =>
  sessao.situacao === SituacaoTranscricaoType.Concluida && !!sessao.textoConsolidado?.trim()

const rotuloSituacao = (sessao: TranscricaoAtendimentoView): string =>
  sessao.situacao === SituacaoTranscricaoType.Concluida
    ? 'Sem falas transcritas'
    : SITUACAO_SEM_TEXTO[sessao.situacao] ?? 'Indisponível'

const tituloSessao = (sessao: TranscricaoAtendimentoView, numero: number): string => {
  const fim = formatHoraMinuto(sessao.dtFim)
  const duracao = formatDuracaoSegundos(sessao.duracaoSegundos)
  return (
    `Sessão ${numero} · ${formatDateTime(sessao.dtInicio)}` +
    (fim ? ` – ${fim}` : '') +
    (duracao ? ` · ${duracao}` : '')
  )
}

export const TranscricaoSection: FC<Props> = ({ isLoading, sessoes, erro = false }) => {
  const [expandida, setExpandida] = useState<number | null>(null)

  if (isLoading) {
    return (
      <div className='row mb-4'>
        <label className='col-12 fw-bold text-gray-700 mb-2'>Transcrição da Consulta</label>
        <div className='col-12 text-gray-700 fst-italic' role='status'>
          Carregando transcrição...
        </div>
      </div>
    )
  }

  if (erro) {
    return (
      <div className='row mb-4'>
        <label className='col-12 fw-bold text-gray-700 mb-2'>Transcrição da Consulta</label>
        <div className='col-12' role='alert'>
          <InfoAlert variant='danger' className='mb-0'>
            Não foi possível carregar a transcrição.
          </InfoAlert>
        </div>
      </div>
    )
  }

  if (sessoes.length === 0) {
    return (
      <div className='row mb-4'>
        <label className='col-12 fw-bold text-gray-700 mb-2'>Transcrição da Consulta</label>
        <div className='col-12 text-gray-700 fst-italic'>
          Transcrição não disponível para este atendimento.
        </div>
      </div>
    )
  }

  return (
    <div className='row mb-4'>
      <label className='col-12 fw-bold text-gray-700 mb-2'>Transcrição da Consulta</label>
      <div className='col-12 d-flex flex-column gap-2'>
        {sessoes.map((sessao, indice) => {
          const titulo = tituloSessao(sessao, indice + 1)

          if (!temTexto(sessao)) {
            return (
              <div
                key={sessao.id}
                className='d-flex flex-wrap align-items-center justify-content-between gap-2 p-3 rounded border border-gray-300'
              >
                <span className='text-gray-700 fs-7'>{titulo}</span>
                <span className='badge atd-badge-secondary fs-8'>{rotuloSituacao(sessao)}</span>
              </div>
            )
          }

          const aberta = expandida === sessao.id
          const idPainel = `transcricao-sessao-${sessao.id}`
          const idCabecalho = `${idPainel}-cabecalho`

          return (
            <div key={sessao.id} className='rounded border border-gray-300'>
              <button
                type='button'
                id={idCabecalho}
                aria-expanded={aberta}
                aria-controls={idPainel}
                onClick={() => setExpandida(aberta ? null : sessao.id)}
                className='btn btn-flush w-100 d-flex align-items-center justify-content-between gap-2 p-3 text-start'
              >
                <span className='fw-semibold text-gray-800 fs-7'>{titulo}</span>
                <span className='d-flex align-items-center gap-2'>
                  <i
                    className={`fas fa-chevron-${aberta ? 'up' : 'down'} text-gray-700`}
                    aria-hidden='true'
                  />
                </span>
              </button>
              {aberta && (
                <div
                  id={idPainel}
                  role='region'
                  aria-labelledby={idCabecalho}
                  className='px-3 pb-3'
                >
                  <ConversaTranscricao texto={sessao.textoConsolidado} />
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
